/* eslint-disable max-lines */
import React, { useMemo } from 'react';
import styled from 'styled-components/macro';
import { BookingPriceBreakdown } from '../Utils/graphql-sdk';
import { Row } from '../SubComponents/Modals';
import { Text, Label } from '../SubComponents/Text';
import useLocalize from '../Hooks/useLocalize';
import { space } from 'styled-system';
import useFormatPrice from '../Hooks/use-format-price';
import RemoveItemButton from './remove-item-button';
import RemoveCancellationWarrantyButton from './remove-cancellation-warranty-button';

const OrderWrapper = styled.div<{ mb?: string }>`
  display: flex;
  flex-direction: column;
  ${space}
`;

const OrderEntryWrapper = styled.span`
  display: flex;
  flex-direction: row;
`;
const PriceCol = styled.span`
  display: inline-flex;
  align-items: baseline;
  gap: 8px;
`;
const Strike = styled.span`
  text-decoration: line-through;
  opacity: 0.6;
`;
export type AddonPriceBreakdown = {
  addonId: string;
  itemId: string;
  itemName: string;
  quantity: number;
  basePrice: number;
  discount: number;
  priceWithDiscount: number;
};

interface SummaryInfoProps {
  orderEditable?: boolean;
  prices: {
    voucherValue?: number;
    voucherValueAddons?: number;
    sailingPrice?: number;
    extrasPrice?: number;
    warrantyPrice?: number;
    paymentValue?: number;
    fullPrice?: number;
    balance?: number;
    toBePaid?: number;
    toBeRefunded?: number;
  };
  order?: { itemId: string; quantity: number }[];
  items?: { id: string; name: string; price: number; donation?: boolean | null }[];
  vouchers: {
    id: string;
    category: string;
    type?: 'flat' | 'percent';
    value?: number;
  }[];
  payments?: { id: string }[];
  isWarrantyApplied?: boolean;
  warrantyEditable?: boolean;
  textColor?: string;
  addonsBreakdown?: AddonPriceBreakdown[];
  priceBreakdown?: Partial<BookingPriceBreakdown>;
}

const SummaryInfo = ({
  orderEditable,
  prices,
  order = [],
  items = [],
  vouchers = [],
  payments,
  isWarrantyApplied,
  warrantyEditable,
  textColor = '#fff',
  addonsBreakdown,
  priceBreakdown,
}: SummaryInfoProps) => {
  const localize = useLocalize();
  const formatPrice = useFormatPrice();

  const combinedDiscount =
    (priceBreakdown?.voucherValueBoatRental || 0) +
    (priceBreakdown?.voucherValueAddons || 0);

  const addons = useMemo(() => {
    return order
      .filter((addon) => {
        const item = items.find((item) => item.id === addon.itemId);
        return item ? !item.donation : false;
      })
      .sort((a, b) => {
        const itemA = items.find((item) => item.id === a.itemId);
        const itemB = items.find((item) => item.id === b.itemId);

        if (!itemA || !itemB) {
          return 0;
        }

        return itemA?.name.localeCompare(itemB?.name) ?? 0;
      });
  }, [order, items]);
  const donations = useMemo(() => {
    return order
      .filter((addon) => {
        const item = items.find((item) => item.id === addon.itemId);
        return item ? item.donation === true : false;
      })
      .sort((a, b) => {
        const itemA = items.find((item) => item.id === a.itemId);
        const itemB = items.find((item) => item.id === b.itemId);

        if (!itemA || !itemB) {
          return 0;
        }

        return itemA?.name.localeCompare(itemB?.name) ?? 0;
      });
  }, [order, items]);
  const hasBreakdown = Array.isArray(addonsBreakdown) && addonsBreakdown.length > 0;

  return (
    <>
      {priceBreakdown?.boatRentalPriceWithDiscount !== undefined ? (
        <Row justifyContent="space-between" mb="3">
          <Text color={textColor}>{localize('product.priceForSailing')}:</Text>
          <Text bold color={textColor}>
            {formatPrice(priceBreakdown.boatRentalPriceWithDiscount)}
          </Text>
        </Row>
      ) : prices.sailingPrice !== undefined ? (
        <Row justifyContent="space-between" mb="3">
          <Text color={textColor}>{localize('product.priceForSailing')}:</Text>
          <Text bold color={textColor}>
            {formatPrice(prices.sailingPrice)}
          </Text>
        </Row>
      ) : null}
      {(hasBreakdown || (addons.length > 0 && items.length > 0)) && (
        <>
          <Label color={textColor} mb="2" mt="2">
            {localize('product.extras')}
          </Label>
          <OrderWrapper>
            {hasBreakdown
              ? addonsBreakdown!
                  .slice()
                  .sort((a, b) => a.itemName.localeCompare(b.itemName))
                  .map((row) => {
                    const baseTotal = row.basePrice;
                    const hasDiscount =
                      row.discount > 0 && row.priceWithDiscount < baseTotal;
                    return (
                      <Row key={row.addonId} justifyContent="space-between" mb="2">
                        <OrderEntryWrapper>
                          {orderEditable && <RemoveItemButton itemId={row.itemId} />}
                          <Text monospaced color={textColor}>
                            {row.quantity} &times; {row.itemName}
                          </Text>
                        </OrderEntryWrapper>
                        <PriceCol>
                          {hasDiscount && (
                            <Text color={textColor}>
                              <Strike>{formatPrice(baseTotal)}</Strike>
                            </Text>
                          )}
                          <Text color={textColor}>
                            {formatPrice(row.priceWithDiscount)}
                          </Text>
                        </PriceCol>
                      </Row>
                    );
                  })
              : addons.map((addon) => {
                  const item = items.find((i) => i.id === addon.itemId);
                  const line = (item?.price ?? 0) * addon.quantity;
                  return (
                    <Row key={addon.itemId} justifyContent="space-between" mb="2">
                      <OrderEntryWrapper>
                        {orderEditable && <RemoveItemButton itemId={addon.itemId} />}
                        <Text monospaced color={textColor}>
                          {addon.quantity} &times; {item?.name}
                        </Text>
                      </OrderEntryWrapper>
                      <Text color={textColor}>{formatPrice(line)}</Text>
                    </Row>
                  );
                })}
          </OrderWrapper>
        </>
      )}
      {priceBreakdown?.addonsPriceWithDiscount !== undefined ? (
        <Row justifyContent="space-between" mb="3">
          <Text color={textColor}>{localize('product.priceForExtras')}:</Text>
          <Text bold color={textColor}>
            {formatPrice(priceBreakdown.addonsPriceWithDiscount)}
          </Text>
        </Row>
      ) : prices.extrasPrice !== undefined ? (
        <Row justifyContent="space-between" mb="3">
          <Text color={textColor}>{localize('product.priceForExtras')}:</Text>
          <Text bold color={textColor}>
            {formatPrice(prices.extrasPrice)}
          </Text>
        </Row>
      ) : null}

      {combinedDiscount > 0 ? (
        <Row justifyContent="space-between" mb="2">
          <Text color={textColor} capitalize>
            {vouchers[0]?.category
              ? `${localize(`product.${vouchers[0].category}`)}${
                  vouchers[0]?.type === 'percent' && vouchers[0]?.value
                    ? ` (${vouchers[0].value}%)`
                    : ''
                }`
              : localize('product.giftcard')}
          </Text>
          <Text color={textColor}>{formatPrice(-combinedDiscount)}</Text>
        </Row>
      ) : null}

      {donations.length > 0 && items.length > 0 && (
        <>
          <Label color={textColor} mb="2" mt="2">
            Donations{/* TODO: localize this */}
          </Label>
          <OrderWrapper mb="3">
            {donations.map((addon) => (
              <Row key={addon.itemId} justifyContent="space-between" mb="2">
                <OrderEntryWrapper>
                  {orderEditable && <RemoveItemButton itemId={addon.itemId} />}
                  <Text monospaced color={textColor}>
                    {addon.quantity} &times;{' '}
                    {items.find((item) => item.id === addon.itemId)?.name}
                  </Text>
                </OrderEntryWrapper>
                <Text color={textColor} position="relative">
                  {formatPrice(
                    (items.find((item) => item.id === addon.itemId)?.price ?? 0) *
                      addon.quantity
                  )}
                </Text>
              </Row>
            ))}
          </OrderWrapper>
        </>
      )}

      {priceBreakdown?.cancellationWarrantyPrice ? (
        <Row justifyContent="space-between" mb="2">
          <OrderEntryWrapper>
            {warrantyEditable && <RemoveCancellationWarrantyButton />}
            <Text color={textColor}>{localize('product.cancellationWarranty')}</Text>
          </OrderEntryWrapper>
          <Text color={textColor}>
            {formatPrice(priceBreakdown.cancellationWarrantyPrice)}
          </Text>
        </Row>
      ) : prices.warrantyPrice && isWarrantyApplied ? (
        <Row justifyContent="space-between" mb="2">
          <OrderEntryWrapper>
            {warrantyEditable && <RemoveCancellationWarrantyButton />}
            <Text color={textColor}>{localize('product.cancellationWarranty')}</Text>
          </OrderEntryWrapper>

          <Text color={textColor}>{formatPrice(prices.warrantyPrice)}</Text>
        </Row>
      ) : null}

      {priceBreakdown?.totalPrice !== undefined ? (
        <Row justifyContent="space-between">
          <Text color={textColor} nowrap mr="10px">
            {localize('price.allIncluded')}:
          </Text>
          <PriceCol>
            <Text bold color={textColor} nowrap>
              {formatPrice(priceBreakdown.totalPrice)}
            </Text>
          </PriceCol>
        </Row>
      ) : (
        <Row justifyContent="space-between">
          <Text color={textColor} nowrap mr="10px">
            {localize('price.allIncluded')}:
          </Text>
          <PriceCol>
            <Text bold color={textColor}>
              {prices.fullPrice !== undefined ? formatPrice(prices.fullPrice) : '-'}
            </Text>
          </PriceCol>
        </Row>
      )}

      {(prices.balance || prices.balance === 0) &&
      (prices.toBeRefunded || prices.toBeRefunded === 0) &&
      payments &&
      payments.length > 0 ? (
        <Row justifyContent="space-between" mt="3">
          <Text color={textColor}>
            {prices.balance > 0
              ? localize('price.toBeRefunded')
              : localize('price.toBePaid')}
            :
          </Text>
          <Text bold color={textColor}>
            {prices.balance > 0
              ? formatPrice(prices.toBeRefunded)
              : formatPrice(prices.toBePaid ?? 0)}
          </Text>
        </Row>
      ) : null}
    </>
  );
};

export default SummaryInfo;
